import React, { useState } from 'react';
import { Camera, Play, Download, ExternalLink, Image, Music, Video, Upload } from 'lucide-react';

const MediaGallery = ({ artistId }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Mock media data - in real app, this would come from Supabase storage
  const mediaItems = [
    {
      id: 1,
      type: 'image',
      title: 'Concert à Paris - 2024',
      url: '/api/placeholder/400/300',
      thumbnail: '/api/placeholder/200/150',
      description: 'Performance live au Zénith de Paris',
      uploadDate: '2024-03-15',
      tags: ['live', 'paris', 'concert']
    },
    {
      id: 2,
      type: 'video',
      title: 'Teaser nouveau single',
      url: '/api/placeholder/400/300',
      thumbnail: '/api/placeholder/200/150',
      duration: '02:34',
      description: 'Extrait du prochain album',
      uploadDate: '2024-03-10',
      tags: ['studio', 'single', 'preview']
    },
    {
      id: 3,
      type: 'audio',
      title: 'DJ Set - Festival été 2023',
      url: '#',
      thumbnail: '/api/placeholder/200/150',
      duration: '45:12',
      description: 'Mix complet du festival d\'été',
      uploadDate: '2023-08-20',
      tags: ['dj-set', 'festival', 'mix']
    },
    {
      id: 4,
      type: 'image',
      title: 'Backstage studio',
      url: '/api/placeholder/400/300',
      thumbnail: '/api/placeholder/200/150',
      description: 'Séance d\'enregistrement en studio',
      uploadDate: '2024-02-28',
      tags: ['studio', 'backstage', 'recording']
    },
    {
      id: 5,
      type: 'video',
      title: 'Interview exclusive',
      url: '/api/placeholder/400/300',
      thumbnail: '/api/placeholder/200/150',
      duration: '08:45',
      description: 'Entretien sur les projets à venir',
      uploadDate: '2024-01-15',
      tags: ['interview', 'media', 'projects']
    },
    {
      id: 6,
      type: 'image',
      title: 'Photo promo 2024',
      url: '/api/placeholder/400/300',
      thumbnail: '/api/placeholder/200/150',
      description: 'Session photo promotionnelle officielle',
      uploadDate: '2024-01-10',
      tags: ['promo', 'officiel', 'portrait']
    }
  ];

  const filterOptions = [
    { id: 'all', label: 'Tout', icon: Camera },
    { id: 'image', label: 'Photos', icon: Image },
    { id: 'video', label: 'Vidéos', icon: Video },
    { id: 'audio', label: 'Audio', icon: Music }
  ];

  const filteredMedia = activeFilter === 'all' 
    ? mediaItems 
    : mediaItems?.filter(item => item?.type === activeFilter);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'audio':
        return <Music className="w-4 h-4" />;
      default:
        return <Camera className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Galerie média</h2>
        <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          <Upload className="w-4 h-4 mr-2" />
          Ajouter des médias
        </button>
      </div>
      {/* Filter Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        {filterOptions?.map((option) => {
          const IconComponent = option?.icon;
          return (
            <button
              key={option?.id}
              onClick={() => setActiveFilter(option?.id)}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeFilter === option?.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <IconComponent className="w-4 h-4 mr-2" />
              {option?.label}
              <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                {option?.id === 'all' 
                  ? mediaItems?.length 
                  : mediaItems?.filter(item => item?.type === option?.id)?.length
                }
              </span>
            </button>
          );
        })}
      </div>
      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMedia?.map((item) => (
          <div
            key={item?.id}
            className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedMedia(item)}
          >
            {/* Media Thumbnail */}
            <div className="relative aspect-video bg-gray-100">
              <img
                src={item?.thumbnail}
                alt={item?.title}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                <div className="opacity-0 hover:opacity-100 transition-opacity">
                  {item?.type === 'video' ? (
                    <Play className="w-12 h-12 text-white" />
                  ) : item?.type === 'audio' ? (
                    <Music className="w-12 h-12 text-white" />
                  ) : (
                    <ExternalLink className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>

              {/* Type Badge */}
              <div className="absolute top-2 left-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-white ${
                  item?.type === 'image' ? 'bg-green-600' :
                  item?.type === 'video' ? 'bg-red-600' :
                  item?.type === 'audio' ? 'bg-purple-600' : 'bg-gray-600'
                }`}>
                  {getTypeIcon(item?.type)}
                  <span className="ml-1 capitalize">{item?.type}</span>
                </span>
              </div>

              {/* Duration for video/audio */}
              {item?.duration && (
                <div className="absolute bottom-2 right-2">
                  <span className="bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    {item?.duration}
                  </span>
                </div>
              )}
            </div>

            {/* Media Info */}
            <div className="p-4">
              <h3 className="font-medium text-gray-900 mb-1 line-clamp-1">
                {item?.title}
              </h3>
              <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                {item?.description}
              </p>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-3">
                {item?.tags?.slice(0, 3)?.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    #{tag}
                  </span>
                ))}
                {item?.tags?.length > 3 && (
                  <span className="text-xs text-gray-500">+{item?.tags?.length - 3}</span>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {new Date(item.uploadDate)?.toLocaleDateString('fr-FR')}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      // Handle download
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      // Handle external link
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {filteredMedia?.length === 0 && (
        <div className="text-center py-12">
          <Camera className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun média trouvé
          </h3>
          <p className="text-gray-500 mb-4">
            Aucun média ne correspond aux filtres sélectionnés.
          </p>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            <Upload className="w-4 h-4 mr-2" />
            Ajouter des médias
          </button>
        </div>
      )}
      {/* Media Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium">{selectedMedia?.title}</h3>
              <button
                onClick={() => setSelectedMedia(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <img
                src={selectedMedia?.url}
                alt={selectedMedia?.title}
                className="w-full h-auto max-h-96 object-contain mx-auto"
              />
              <div className="mt-4">
                <p className="text-gray-600 mb-2">{selectedMedia?.description}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMedia?.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;